import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Circle,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Layers,
  Siren,
  Radio,
  Navigation,
  Wifi,
  ChevronUp,
  ChevronDown,
  Search,
  Crosshair,
  Droplets,
  Flame,
  Building2,
  Truck,
  Activity,
  Command,
} from "lucide-react";
import { useSelector } from "react-redux";

// --- LEAFLET ICON FIX ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom Icons for Layers
const createCustomIcon = (color, size = 14) =>
  new L.DivIcon({
    className: "custom-icon",
    html: `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 10px ${color};"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

// Icon for Field Units
const unitIcon = new L.DivIcon({
  className: "unit-icon",
  html: `<div style="background-color: #3b82f6; width: 14px; height: 14px; transform: rotate(45deg); border: 2px solid white; box-shadow: 0 0 8px #3b82f6;"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// --- GOA GIS DATA (Mainland & Coastal) ---
const GOA_CENTER = [15.45, 73.95]; // Centered to capture both North, South, and Inland Goa

// 1. Establishments (Critical Infrastructure, Hospitals, Govt Buildings)
const establishmentData = [
  {
    id: "E1",
    pos: [15.454, 73.836],
    name: "Goa Medical College",
    type: "Hospital",
  }, // Bambolim
  {
    id: "E2",
    pos: [15.525, 73.824],
    name: "State Secretariat",
    type: "Govt Admin",
  }, // Porvorim
  {
    id: "E3",
    pos: [15.283, 73.965],
    name: "South Goa District Hospital",
    type: "Hospital",
  }, // Margao
  {
    id: "E4",
    pos: [15.498, 73.827],
    name: "Police Headquarters",
    type: "Law Enforcement",
  }, // Panjim
  {
    id: "E5",
    pos: [15.3981, 73.8111],
    name: "Mormugao Port Trust",
    type: "Infrastructure",
  }, // Vasco
  {
    id: "E6",
    pos: [15.402, 74.018],
    name: "Ponda Sub-District Hospital",
    type: "Hospital",
  }, // Ponda (Mainland)
  {
    id: "E7",
    pos: [15.5312, 74.1353],
    name: "Valpoi Emergency Center",
    type: "Emergency Ops",
  }, // Valpoi (Mainland)
];

// 2. Water Sources (Dams, Treatment Plants, Reservoirs)
const waterSourcesData = [
  {
    id: "W1",
    pos: [15.215, 74.17],
    radius: 1500,
    name: "Salaulim Dam",
    type: "Reservoir",
  }, // Sanguem
  {
    id: "W2",
    pos: [15.61, 74.088],
    radius: 1000,
    name: "Anjunem Dam",
    type: "Reservoir",
  }, // Chorla Ghat area
  {
    id: "W3",
    pos: [15.415, 74.035],
    radius: 600,
    name: "Opa Water Treatment Plant",
    type: "Treatment",
  }, // Ponda
  {
    id: "W4",
    pos: [15.51, 73.87],
    radius: 1200,
    name: "Mandovi River Intake",
    type: "River Basin",
  },
];

// 3. Access Roads (Major Highways - NH66, Ponda Highway, etc.)
const accessRoadsData = [
  // NH66 (North to South)
  [
    [15.71, 73.81],
    [15.59, 73.82],
    [15.52, 73.83],
    [15.45, 73.85],
    [15.28, 73.96],
    [15.0, 74.04],
  ],
  // Panjim to Ponda (Mainland connection)
  [
    [15.498, 73.827],
    [15.45, 73.9],
    [15.402, 74.018],
  ],
  // Ponda to Margao
  [
    [15.402, 74.018],
    [15.35, 73.99],
    [15.283, 73.965],
  ],
];

// 4. Gas Lines (Pipeline networks)
const gasLinesData = [
  // Vasco to Zuari/Verna industrial belt
  [
    [15.3981, 73.8111],
    [15.37, 73.88],
    [15.35, 73.92],
  ],
  // Ponda industrial network
  [
    [15.42, 73.95],
    [15.4, 74.018],
    [15.38, 74.05],
  ],
];

// 5. Units (Emergency Response Teams)
const generateUnits = () => {
  return Array.from({ length: 12 }).map((_, i) => ({
    id: `CMD-${101 + i}`,
    status: i < 7 ? "online" : i < 10 ? "busy" : "offline",
    type: i % 3 === 0 ? "Fire" : i % 3 === 1 ? "Police" : "Medical",
    // Spread units across Goa including mainland
    pos: [
      15.45 + (Math.random() * 0.3 - 0.15),
      73.95 + (Math.random() * 0.2 - 0.1),
    ],
  }));
};

// Live Alerts (Command Centre Context)
const liveAlerts = [
  {
    id: 1,
    time: "14:42:15",
    msg: "Gas Pressure Drop Detected",
    level: "critical",
    loc: "Verna Industrial",
    coords: [15.35, 73.92],
  },
  {
    id: 2,
    time: "14:40:05",
    msg: "Traffic Blockade (Accident)",
    level: "warning",
    loc: "NH66 Porvorim",
    coords: [15.52, 73.83],
  },
  {
    id: 3,
    time: "14:38:22",
    msg: "Water Level Normalizing",
    level: "info",
    loc: "Salaulim Dam",
    coords: [15.215, 74.17],
  },
  {
    id: 4,
    time: "14:35:10",
    msg: "Power Outage",
    level: "critical",
    loc: "Ponda Sub-District",
    coords: [15.402, 74.018],
  },
  {
    id: 5,
    time: "14:30:00",
    msg: "Ambulance Dispatched",
    level: "success",
    loc: "Vasco Port",
    coords: [15.3981, 73.8111],
  },
];

const incidentTimeline = [
  { time: "14:45", status: "Emergency Response Dispatched", active: true },
  { time: "14:42", status: "Main Gas Valve Shut Off", active: true },
  { time: "14:40", status: "Automated Alert Triggered", active: true },
  { time: "12:00", status: "Routine Infrastructure Sync", active: false },
];

// --- MAP CONTROLLER COMPONENT ---
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || 11, { duration: 1.5 });
    }
  }, [center, zoom, map]);
  return null;
};

// --- MAIN COMPONENT ---
const CommandCentreContent = () => {
  const [activeLayers, setActiveLayers] = useState({
    establishments: true,
    waterSources: true,
    accessRoads: true,
    gasLines: true,
    units: true,
  });

  const themeMode = useSelector((state) => state.theme?.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
  const scrollBg = themeMode ? "sidebar_scroll_dark" : "sidebar_scroll";

  const [mapConfig, setMapConfig] = useState({ center: GOA_CENTER, zoom: 11 });
  const [units, setUnits] = useState(generateUnits());
  const [searchTerm, setSearchTerm] = useState("");
  const [isPanelExpanded, setIsPanelExpanded] = useState(true);

  const toggleLayer = (layer) => {
    setActiveLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
  };

  const handleAlertClick = (coords) => {
    if (coords) {
      setMapConfig({ center: coords, zoom: 15 });
    }
  };

  const handleUnitClick = (unit) => {
    if (unit.status !== "offline") {
      setMapConfig({ center: unit.pos, zoom: 15 });
    }
  };

  const filteredUnits = units.filter(
    (u) =>
      u.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.type.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="bg-slate-950 h-screen w-full flex flex-col overflow-hidden text-slate-100 font-sans selection:bg-emerald-500/30">
      {/* HEADER */}
      <div
        className={`h-14 border-b ${borderThem} ${bgColorClass} ${textColorClass} backdrop-blur-sm flex justify-between items-center px-6 z-30`}
      >
        <div
          className={`flex ps-3 md:ps-0 ${textColorClass} text-sm md:text-base items-center gap-x-2`}
        >
          <Command size={16} />
          <p className="font-semibold">COMMAND CENTRE DASHBOARD</p>
        </div>
        <div className="flex items-center gap-4 text-xs font-mono ">
          <span className="flex items-center gap-1">
            <Wifi size={10} /> GIS SENSORS: ONLINE
          </span>
          <span>GOA SECTOR (MAINLAND & COASTAL)</span>
        </div>
      </div>

      <div className="relative flex-1 w-full overflow-hidden">
        {/* 1. GIS MAP */}
        <div className="absolute inset-0 z-0">
          <MapContainer
            center={GOA_CENTER}
            zoom={11}
            style={{ height: "100%", width: "100%", background: "#0f172a" }}
            zoomControl={false}
          >
            <TileLayer
              attribution="&copy; CARTO"
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            <MapController center={mapConfig.center} zoom={mapConfig.zoom} />

            {/* LAYER: Establishments */}
            {activeLayers.establishments &&
              establishmentData.map((est) => (
                <Marker
                  key={est.id}
                  position={est.pos}
                  icon={createCustomIcon("#a855f7")}
                >
                  <Popup className="custom-popup">
                    <strong className="text-slate-900">{est.name}</strong>
                    <br />
                    <span className="text-purple-600 text-xs font-bold">
                      {est.type}
                    </span>
                  </Popup>
                </Marker>
              ))}

            {/* LAYER: Water Sources */}
            {activeLayers.waterSources &&
              waterSourcesData.map((water) => (
                <Circle
                  key={water.id}
                  center={water.pos}
                  radius={water.radius}
                  pathOptions={{
                    color: "#0ea5e9",
                    fillColor: "#0ea5e9",
                    fillOpacity: 0.2,
                    weight: 2,
                  }}
                >
                  <Popup>
                    {water.name} - {water.type}
                  </Popup>
                </Circle>
              ))}

            {/* LAYER: Access Roads */}
            {activeLayers.accessRoads && (
              <Polyline
                positions={accessRoadsData}
                pathOptions={{ color: "#4ade80", weight: 4, opacity: 0.6 }}
              />
            )}

            {/* LAYER: Gas Lines */}
            {activeLayers.gasLines && (
              <Polyline
                positions={gasLinesData}
                pathOptions={{
                  color: "#f97316",
                  weight: 3,
                  dashArray: "10, 10",
                  opacity: 0.8,
                }}
              />
            )}

            {/* MOVING UNITS LAYER (For Dept Coordination) */}
            {activeLayers.units &&
              units
                .filter((u) => u.status !== "offline")
                .map((u) => (
                  <Marker key={u.id} position={u.pos} icon={unitIcon}>
                    <Popup className="custom-popup">
                      <strong className="text-blue-900">{u.id}</strong>
                      <br />
                      <span className="text-slate-600">
                        {u.type} Response Unit
                      </span>
                    </Popup>
                  </Marker>
                ))}
          </MapContainer>
        </div>

        {/* 2. GIS MAP LAYERS PANEL (Matches Image Specs exactly) */}
        <div className="absolute top-6 left-6 z-[1000]">
          <div className="backdrop-blur-md bg-slate-900/90 border border-slate-700/50 p-4 rounded-xl shadow-2xl w-64">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Layers size={14} /> GIS Map Layers
            </h3>
            <div className="space-y-2">
              {[
                {
                  id: "establishments",
                  label: "Establishments",
                  color: "bg-purple-500",
                  icon: Building2,
                },
                {
                  id: "waterSources",
                  label: "Water sources",
                  color: "bg-sky-500",
                  icon: Droplets,
                },
                {
                  id: "accessRoads",
                  label: "Access roads",
                  color: "bg-green-500",
                  icon: Navigation,
                },
                {
                  id: "gasLines",
                  label: "Gas lines",
                  color: "bg-orange-500",
                  icon: Flame,
                },
              ].map((layer) => (
                <label
                  key={layer.id}
                  className="flex items-center justify-between cursor-pointer group hover:bg-white/5 p-1.5 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <layer.icon size={14} className="text-slate-400" />
                    <span className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors">
                      {layer.label}
                    </span>
                  </div>
                  <div
                    onClick={() => toggleLayer(layer.id)}
                    className={`w-8 h-4 rounded-full relative transition-colors ${activeLayers[layer.id] ? layer.color : "bg-slate-700"}`}
                  >
                    <div
                      className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform shadow-md`}
                      style={{ left: activeLayers[layer.id] ? "18px" : "2px" }}
                    ></div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* 3. WIDGETS: LIVE ALERTS STREAM & INCIDENT TIMELINE */}
        <div className="absolute top-6 right-6 bottom-32 z-[1000] w-72 flex flex-col gap-3 pointer-events-none">
          {/* Live Alerts Stream Widget */}
          <div className="pointer-events-auto flex-1 backdrop-blur-md bg-slate-900/90 border border-slate-700/50 rounded-xl overflow-hidden flex flex-col shadow-2xl">
            <div className="p-3 border-b border-white/5 bg-white/5 flex justify-between items-center">
              <h3 className="text-xs font-bold text-red-400 flex items-center gap-2">
                <Siren size={14} className="animate-pulse" /> LIVE ALERTS STREAM
              </h3>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping"></span>
            </div>
            <div
              className={`overflow-y-auto ${scrollBg}  flex-1 p-2 space-y-2 scrollbar-hide`}
            >
              {liveAlerts.map((alert) => (
                <div
                  key={alert.id}
                  onClick={() => handleAlertClick(alert.coords)}
                  className="group relative bg-black/40 hover:bg-slate-800 border-l-2 border-transparent hover:border-red-500 transition-all rounded p-2 cursor-pointer"
                >
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-0.5 rounded-l ${alert.level === "critical" ? "bg-red-500" : "bg-amber-500"}`}
                  ></div>
                  <div className="ml-2">
                    <div className="flex justify-between items-start mb-0.5">
                      <span
                        className={`text-[9px] font-bold uppercase px-1 rounded ${alert.level === "critical" ? "bg-red-900/50 text-red-400" : "bg-slate-700 text-slate-300"}`}
                      >
                        {alert.level}
                      </span>
                      <span className="text-[9px] text-slate-500">
                        {alert.time}
                      </span>
                    </div>
                    <p className="text-xs font-medium text-slate-200 group-hover:text-white mb-0.5">
                      {alert.msg}
                    </p>
                    <p className="text-[9px] text-slate-500 flex items-center gap-1">
                      <Crosshair size={8} /> {alert.loc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Incident Timeline Widget */}
          <div className="pointer-events-auto h-32 backdrop-blur-md bg-slate-900/90 border border-slate-700/50 rounded-xl overflow-hidden flex flex-col">
            <div className="p-2 border-b border-white/5 bg-white/5">
              <h3 className="text-[10px] font-bold text-slate-300 uppercase">
                Incident Timeline
              </h3>
            </div>
            <div className={`p-3 ${scrollBg} overflow-y-auto`}>
              <div className="relative pl-3 border-l border-slate-700 space-y-3">
                {incidentTimeline.map((step, idx) => (
                  <div key={idx} className="relative">
                    <div
                      className={`absolute -left-[17px] top-1 w-2 h-2 rounded-full border border-slate-900 ${step.active ? "bg-emerald-500" : "bg-slate-600"}`}
                    ></div>
                    <p
                      className={`text-[10px] font-bold ${step.active ? "text-emerald-400" : "text-slate-500"}`}
                    >
                      {step.status}
                    </p>
                    <p className="text-[9px] text-slate-600">{step.time}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 4. DEPARTMENT COORDINATION PANEL */}
        <div
          className={`absolute bottom-2 left-6 right-6 z-[1000] transition-all duration-300 ${isPanelExpanded ? "translate-y-0" : "translate-y-[calc(100%-80px)]"}`}
        >
          <div className="backdrop-blur-xl bg-slate-900/95 border border-slate-700/50 rounded-2xl px-4 py-2 shadow-2xl flex items-center gap-4">
            {/* Stats */}
            <div className="flex flex-col border-r border-white/10 pr-4 min-w-[120px]">
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                Dept Coordination
              </span>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xl font-bold text-white">
                  {filteredUnits.filter((u) => u.status !== "offline").length}
                </span>
                <div className="flex flex-col">
                  <span className="text-[10px] text-emerald-400 font-medium flex items-center gap-1">
                    <Activity size={8} /> Active Teams
                  </span>
                </div>
              </div>
            </div>

            {/* Search Input for Units */}
            {isPanelExpanded && (
              <div className="bg-black/40 border border-white/10 p-2 rounded-lg flex items-center gap-2 text-slate-400 min-w-[150px]">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Filter unit..."
                  className="bg-transparent border-none focus:outline-none text-xs w-full text-white placeholder:text-slate-600"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}

            {/* Scrollable Unit List */}
            {isPanelExpanded && (
              <div
                className={`flex-1 overflow-x-auto ${scrollBg} flex gap-3 scrollbar-hide py-1 pl-2 border-l border-white/10`}
              >
                {filteredUnits.map((unit) => (
                  <div
                    key={unit.id}
                    onClick={() => handleUnitClick(unit)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-all cursor-pointer min-w-[130px] hover:scale-105 active:scale-95
                      ${
                        unit.status === "online"
                          ? "bg-emerald-950/20 border-emerald-500/20 hover:bg-emerald-500/10 hover:border-emerald-500/50"
                          : unit.status === "busy"
                            ? "bg-amber-950/20 border-amber-500/20 hover:border-amber-500/50"
                            : "bg-slate-800/50 border-white/5 opacity-50"
                      }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${unit.status === "online" ? "bg-emerald-400 shadow-[0_0_8px_#34d399]" : unit.status === "busy" ? "bg-amber-400" : "bg-slate-500"}`}
                    ></div>
                    <div>
                      <p className="text-xs font-bold text-slate-200">
                        {unit.id}
                      </p>
                      <p className="text-[10px] text-slate-500 uppercase">
                        {unit.type}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Expand / Collapse Button */}
            <div className="flex gap-3 pl-2 border-l border-white/10">
              <button
                onClick={() => setIsPanelExpanded(!isPanelExpanded)}
                className="flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-800 hover:bg-slate-700 border border-white/10 text-white transition-all hover:scale-105 active:scale-95"
              >
                {isPanelExpanded ? (
                  <ChevronDown size={20} />
                ) : (
                  <ChevronUp size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandCentreContent;
